<template>
  <div>
    <history-modal-content></history-modal-content>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img v-if="currentRoutePermissions.add_saleagentinvoice"
             src="@/assets/images/common-icons/icon_create.svg" @click="createInvoice"
             :title="$t('buttons.agents.create_invoice')">
        <img v-if="currentRoutePermissions.delete_saleagentinvoice"
             src="@/assets/images/common-icons/icon_delete.svg" @click="deleteInvoice"
             :title="$t('buttons.agents.delete_invoice')">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{$t('labels.common.seller')}}</label>
            <multiselect v-model="searchData.sellers" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="agents" :multiple="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.status')}}</label>
            <multiselect v-model="searchData.status" :max-height="200"
                         placeholder=""
                         :options="statuses" :multiple="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.agents.agent_invoice_number')}}</label>
            <input class="form-control" v-model="searchData.agent_invoice_number" @keyup.enter="search"
                   placeholder="">
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="searchData.buyers" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="customers" :multiple="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.invoices.official_sum_payment_status')}}</label>
            <multiselect v-model="searchData.paid_status_official" :max-height="200"
                         :multiple="true" :close-on-select="false"
                         placeholder="" label="name" track-by="key"
                         :options="paymentStatuses"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.currency')}}</label>
            <multiselect v-model="searchData.currencies" :max-height="200"
                         placeholder="" label="code" track-by="code"
                         :options="currencies" :multiple="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <label>{{$t('labels.common.creation_date')}}</label>
            <range-date-picker v-model="searchData.creation_date" range lang="en"
                               placeholder=""
                               range-separator="/" :first-day-of-week="1"></range-date-picker>
          </b-col>
          <b-col cols="4">
            <label>{{$t('labels.invoices.additional_sum_payment_status')}}</label>
            <multiselect v-model="searchData.paid_status_additional" :max-height="200"
                         :multiple="true" :close-on-select="false"
                         placeholder="" label="name" track-by="key"
                         :options="paymentStatuses"></multiselect>
          </b-col>
        </b-row>
      </div>
      <div>
        <b-row>
          <b-col cols="4">
            <input class="form-control"
                   placeholder=""
                   @input="setQuickFilter">
          </b-col>
          <b-col cols="4" offset="4" class="vertical-center-container d-flex justify-content-end">
            <label for="pagination-toggle" class="margin-right-5">{{$t('labels.common.pagination')}}</label>
            <input id="pagination-toggle" type="checkbox"
                   title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                   @change="togglePagination">
            <save-columns-btn :grid="gridOptions" :grid-name="gridName"
                              class="margin-left-5"></save-columns-btn>
          </b-col>
        </b-row>
      </div>
      <div>
        <b-row>
          <b-col>
            <ag-grid-vue class="ag-theme-balham grid" :gridOptions="gridOptions"
                         @rowDoubleClicked="goToInvoice" :pinnedBottomRowData="pinnedRowData"
                         :columnDefs="columns" :rowData="invoices" :postProcessPopup="postProcessPopup">
            </ag-grid-vue>
          </b-col>
        </b-row>
        <b-row class="pagination__wrapper" v-if="paginationState">
          <b-col cols="5">
            <div class="pagination-info">{{countOfViewInvoices}} {{$t('common_content.of')}} {{invoicesCount}}
            </div>
          </b-col>
          <b-col cols="7">
            <b-pagination size="md" :total-rows="invoicesCount" :per-page="searchObject.limit"
                          v-model="currentPaginateIndex"
                          @input="changePaginateState"
            ></b-pagination>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapGetters, mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import RangeDatePicker from 'vue2-datepicker';
  import moment from 'moment';
  import HistoryModalContent from '@/components/modals/HistoryModal';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import {
    cleanFilters,
    saveFilters,
    setFilters,
    checkSavedFilterData,
    getArrayByProperty,
    setNotifyData
  } from '@/helpers/commonHelpers';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { gridMixins } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      RangeDatePicker,
      SaveColumnsBtn,
      HistoryModalContent,
      'ag-grid-vue': AgGridVue
    },
    data() {
      return {
        searchData: {
          sellers: [],
          status: [],
          agent_invoice_number: '',
          buyers: [],
          payment_status: [],
          currencies: [],
          creation_date: '',
          paid_status_official: [],
          paid_status_additional: []
        },
        searchObject: {
          limit: 100,
          offset: 0
        },
        paginationState: true,
        currentPaginateIndex: 1,
        storageKey: 'sales-agent-invoices'
      }
    },
    methods: {
      deleteInvoice() {
        if (this.gridOptions.api.getSelectedRows().length) {
          const id = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id');
          this.$http.delete(`accounting/sale_agent_invoices/`, {body: {ids: id}}).then((response) => {
            this.$store.dispatch('salesAgentsInvoices/loadInvoices');
            this.$notify(setNotifyData('Success', response.body, 'success'));
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          });
        } else {
          this.$notify(setNotifyData('Error', 'Select lots for removing', 'error'));
        }
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      search() {
        this.currentPaginateIndex = 1;
        this.searchObject = {
          ...this.searchObject,
          ...this.searchData,
          status: this.searchData.status.toString(),
          creation_date_between_after: this.searchData.creation_date && this.searchData.creation_date[0] ? moment(this.searchData.creation_date[0]).format(DATE_FORMAT) : '',
          creation_date_between_before: this.searchData.creation_date && this.searchData.creation_date[1] ? moment(this.searchData.creation_date[1]).format(DATE_FORMAT) : '',
          sellers: getArrayByProperty(this.searchData.sellers, 'id').toString(),
          buyers: getArrayByProperty(this.searchData.buyers, 'id').toString(),
          currencies: getArrayByProperty(this.searchData.currencies, 'id').toString(),
          paid_status_official: getArrayByProperty(this.searchData.paid_status_official, 'key').toString(),
          paid_status_additional: getArrayByProperty(this.searchData.paid_status_additional, 'key').toString()
        };
        this.$store.dispatch('salesAgentsInvoices/loadInvoices', this.searchObject);
        saveFilters(this.storageKey, this.searchData);
      },
      createInvoice() {
        this.$router.push({path: `/sales-agent-invoices/new`});
      },
      goToInvoice(e) {
        this.$router.push({path: `/sales-agent-invoices/${e.data.id}`});
      },
      clearData() {
        this.$store.commit('salesAgentsInvoices/setInvoices', [])
      },
      setQuickFilter({target: {value}}) {
        this.gridOptions.api.setQuickFilter(value);
      },
      togglePagination() {
        if (!this.paginationState) {
          delete this.searchObject.limit;
          delete this.searchObject.offset;
        } else {
          this.searchObject.limit = 100;
          this.searchObject.offset = 0;
        }
        this.search();
      },
      changePaginateState() {
        const searchObject = {...this.searchObject, offset: (this.currentPaginateIndex - 1) * this.searchObject.limit};
        this.$store.dispatch('salesAgentsInvoices/loadInvoices', searchObject);
      }
    },
    beforeMount() {
      this.$store.dispatch('salesAgentsInvoices/loadInvoices');
      this.$store.dispatch('generic/getCurrencies');
      this.$store.dispatch('partners/getCustomers');
      this.$store.dispatch('partners/getSaleAgents');
      if (checkSavedFilterData(this.storageKey)) {
        this.searchData = setFilters(this.storageKey);
      }
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    computed: {
      ...mapGetters('salesAgentsInvoices', ['gridOptions']),
      ...mapState('salesAgentsInvoices', ['gridName', 'columns', 'invoices', 'pinnedRowData', 'invoicesCount',
        'paymentStatuses']),
      ...mapState('salesAgentsInvoice', ['statuses']),
      ...mapState('generic', ['currencies', 'currentRoutePermissions']),
      ...mapState('partners', ['customers', 'agents']),
      countOfViewInvoices() {
        return this.invoices.length;
      }
    }
  }
</script>

<style scoped>

  .grid {
    height: calc(100vh - 400px);
  }

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/agents/sales-invoices/SalesAgentInvoicesList.vue